import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Upload, Send, CheckCircle, Clock, AlertCircle, Info, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Record',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  // Entry-level insufficiencies
  'employment_entry': 'Employment Entry',
  'education_entry': 'Education Entry',
  'address_entry': 'Address Entry'
};

// Helper function to get detailed check type display
const getDetailedCheckType = (insuff) => {
  const checkType = insuff.check_type;
  const entryType = insuff.entry_type;
  const entryIndex = insuff.entry_index;
  const entryName = insuff.entry_name;
  
  // Address check/entry
  if (checkType === 'address_check' || checkType === 'address_entry') {
    if (entryIndex === 0 || entryName?.toLowerCase().includes('present')) {
      return 'Present Address';
    } else if (entryIndex === 1 || entryName?.toLowerCase().includes('permanent')) {
      return 'Permanent Address';
    } else if (entryName) {
      return entryName;
    }
    return 'Address Verification';
  }
  
  // Employment check/entry
  if (checkType === 'employment_check' || checkType === 'employment_entry') {
    const companyName = entryName || '';
    // Check if it's current employment (index 0 typically)
    if (entryIndex === 0) {
      return companyName ? `Current Employment - ${companyName}` : 'Current Employment';
    } else {
      return companyName ? `Past Employment - ${companyName}` : `Past Employment ${entryIndex !== undefined ? `#${entryIndex + 1}` : ''}`;
    }
  }
  
  // Education check/entry
  if (checkType === 'education_check' || checkType === 'education_entry') {
    // entryName could be qualification like "Graduation", "Post Graduation", etc.
    const eduType = entryName || '';
    return eduType ? `Education - ${eduType}` : 'Education Verification';
  }
  
  // Default fallback
  return CHECK_NAMES[checkType] || checkType?.replace(/_/g, ' ');
};

export default function EmployeeInsufficiencyTab({ employeeId, caseId, systemInsufficiencies: parentSystemInsufficiencies }) {
  const [manualInsufficiencies, setManualInsufficiencies] = useState([]);
  const [systemInsufficiencies, setSystemInsufficiencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [uploading, setUploading] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    loadData();
  }, [employeeId, caseId]);

  // Use parent's system insufficiencies if provided, otherwise calculate locally
  useEffect(() => {
    if (parentSystemInsufficiencies !== undefined) {
      setSystemInsufficiencies(parentSystemInsufficiencies);
    }
  }, [parentSystemInsufficiencies]);

  const loadData = async () => {
    try {
      // Load manual insufficiencies from QC
      const insuffResponse = await axios.get('/employee/insufficiencies');
      setManualInsufficiencies(insuffResponse.data || []);
      
      // Initialize responses with existing employee responses
      const initialResponses = {};
      (insuffResponse.data || []).forEach(insuff => {
        if (insuff.employee_response || insuff.candidate_response) {
          initialResponses[insuff.id] = insuff.employee_response || insuff.candidate_response;
        }
      });
      setResponses(prev => ({ ...prev, ...initialResponses }));
      
      // Load employee data to detect system insufficiencies (only if parent doesn't provide them)
      if (employeeId && parentSystemInsufficiencies === undefined) {
        const empResponse = await axios.get(`/employees/${employeeId}`);
        setEmployee(empResponse.data);
        detectSystemInsufficiencies(empResponse.data);
      }
    } catch (error) {
      console.log('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Detect missing mandatory fields
  const detectSystemInsufficiencies = (emp) => {
    if (!emp) return;
    
    const detected = [];
    
    // Education entries check
    if (emp.education_entries && emp.education_entries.length > 0) {
      emp.education_entries.forEach((edu, index) => {
        // Roll Number mandatory
        if (!edu.roll_no && !edu.roll_number) {
          detected.push({
            id: `sys_edu_roll_${index}`,
            type: 'system',
            check_type: 'education_check',
            field: 'roll_number',
            reason: 'Roll Number Missing',
            description: `Roll Number is required for ${edu.course || edu.degree || 'Education'} at ${edu.university_name || edu.college || 'Institution'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'education'
          });
        }
      });
    }
    
    // Employment entries check
    if (emp.employment_entries && emp.employment_entries.length > 0) {
      emp.employment_entries.forEach((job, index) => {
        // Employee Code mandatory
        if (!job.employee_code && !job.employee_id) {
          detected.push({
            id: `sys_emp_code_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'employee_code',
            reason: 'Employee Code Missing',
            description: `Employee Code/ID is required for ${job.company_name || 'Company'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // Salary mandatory
        if (!job.salary && !job.salary_per_month && !job.salary_per_annum && !job.ctc) {
          detected.push({
            id: `sys_emp_salary_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'salary',
            reason: 'Salary Information Missing',
            description: `Salary (Per Month/Per Annum) is required for ${job.company_name || 'Company'} (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // UAN/PF Number - Mandatory unless "Not Available" is checked
        if (!job.uan_pf_not_available && !job.uan_pf_number) {
          detected.push({
            id: `sys_emp_uan_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'uan_pf_number',
            reason: 'UAN/PF Number Missing',
            description: `UAN/PF Number is required for ${job.company_name || 'Company'} (Entry ${index + 1}). Check "UAN/PF Number Not Available" if not applicable.`,
            entry_index: index,
            entry_type: 'employment'
          });
        }
        
        // Current employment - Date of Relieving check
        // Only applies to Current employment type
        if (job.employment_type === 'current' && job.can_verify_current === false && !job.date_of_relieving) {
          detected.push({
            id: `sys_emp_relieving_${index}`,
            type: 'system',
            check_type: 'employment_check',
            field: 'date_of_relieving',
            reason: 'Date of Relieving Required',
            description: `Employee still active at ${job.company_name || 'Company'}. Since verification from current employer is not permitted, Date of Relieving is required. (Entry ${index + 1})`,
            entry_index: index,
            entry_type: 'employment',
            remark: 'Employee still active'
          });
        }
        
        // Employment Gap check - check gap between this entry and the NEXT one (chronologically older)
        if (index < emp.employment_entries.length - 1) {
          const nextJob = emp.employment_entries[index + 1]; // Next entry is chronologically older
          const currentStart = job.employed_from;
          const nextEnd = nextJob.employed_to || nextJob.date_of_relieving;
          
          if (currentStart && nextEnd) {
            const startDate = new Date(currentStart);
            const endDate = new Date(nextEnd);
            const gapDays = Math.ceil((startDate - endDate) / (1000 * 60 * 60 * 24));
            
            if (gapDays > 30) {
              // Check if gap reason is provided
              const hasGapReason = job.gap_reason && (job.gap_reason !== 'Other' || job.gap_reason_other);
              if (!hasGapReason) {
                detected.push({
                  id: `sys_emp_gap_${index}`,
                  type: 'system',
                  check_type: 'employment_check',
                  field: 'gap_reason',
                  reason: 'Employment Gap Explanation Required',
                  description: `There is a ${gapDays}-day gap between ${nextJob.company_name || 'Previous Employment'} and ${job.company_name || 'this employment'}. Please provide a reason for this gap. (Entry ${index + 1})`,
                  entry_index: index,
                  entry_type: 'employment',
                  gap_days: gapDays
                });
              }
            }
          }
        }
      });
    }
    
    setSystemInsufficiencies(detected);
  };

  const handleResponseChange = (insuffId, value) => {
    setResponses(prev => ({ ...prev, [insuffId]: value }));
  };

  const handleSubmitResponse = async (insuffId) => {
    const responseText = responses[insuffId];
    if (!responseText) {
      toast.error('Please enter a response');
      return;
    }
    try {
      await axios.post(`/employee/insufficiencies/${insuffId}/respond?response=${encodeURIComponent(responseText)}`);
      toast.success('Response submitted - awaiting QC review');
      loadData();
    } catch (error) {
      toast.error('Failed to submit response');
    }
  };

  // Submit all responses at once
  const handleSubmitAllResponses = async () => {
    // Get only open insufficiencies that have responses to submit
    const openInsufficiencies = manualInsufficiencies.filter(i => 
      i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed'
    );
    
    const responsesToSubmit = openInsufficiencies.filter(insuff => {
      const response = responses[insuff.id];
      const existingResponse = insuff.employee_response || insuff.candidate_response;
      // Only submit if there's a new or different response
      return response && response !== existingResponse;
    });
    
    if (responsesToSubmit.length === 0) {
      toast.info('No new responses to submit');
      return;
    }
    
    setSubmittingAll(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const insuff of responsesToSubmit) {
      try {
        await axios.post(`/employee/insufficiencies/${insuff.id}/respond?response=${encodeURIComponent(responses[insuff.id])}`);
        successCount++;
      } catch (error) {
        console.error(`Error submitting response for ${insuff.id}:`, error);
        errorCount++;
      }
    }
    
    setSubmittingAll(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} response(s) submitted successfully`);
      loadData();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} response(s) failed to submit`);
    }
  };

  const handleFileUpload = async (insuffId, files) => {
    if (!files || files.length === 0) return;
    
    setUploading(prev => ({ ...prev, [insuffId]: true }));
    try {
      const formData = new FormData();
      // Append all files
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      await axios.post(`/employee/insufficiencies/${insuffId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${files.length} document(s) uploaded - awaiting QC review`);
      loadData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [insuffId]: false }));
    }
  };

  const allInsufficiencies = [...manualInsufficiencies, ...systemInsufficiencies];
  // Filter out cleared/closed/resolved insufficiencies from the "pending" count
  const openInsufficiencies = allInsufficiencies.filter(i => 
    i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed'
  );
  // Separate cleared insufficiencies to show them with "Cleared" status
  const clearedInsufficiencies = manualInsufficiencies.filter(i => 
    i.status === 'cleared' || i.status === 'closed' || i.status === 'resolved'
  );
  const hasInsufficiencies = openInsufficiencies.length > 0;

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Action Required</h2>
          <p className="text-slate-600">Review and respond to verification queries</p>
        </div>
        {hasInsufficiencies && (
          <Badge className="bg-rose-100 text-rose-700 text-lg px-4 py-2">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {openInsufficiencies.length} Pending
          </Badge>
        )}
      </div>

      {/* System Detected Insufficiencies */}
      {systemInsufficiencies.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-lg">
              <AlertCircle className="w-5 h-5" />
              System Detected Issues ({systemInsufficiencies.length})
            </CardTitle>
            <p className="text-sm text-amber-700">These fields are missing required information. Please update your profile to resolve.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {systemInsufficiencies.map((insuff) => (
                <div key={insuff.id} className="bg-white p-4 rounded-lg border border-amber-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-amber-900">{insuff.reason}</div>
                      <div className="text-sm text-slate-600 mt-1">{insuff.description}</div>
                      {insuff.remark && (
                        <div className="text-xs text-amber-700 mt-1 italic">Remark: {insuff.remark}</div>
                      )}
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      {CHECK_NAMES[insuff.check_type] || insuff.check_type}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => {
                        // Navigate to the relevant section in profile
                        toast.info(`Please go to "My Details" tab and update ${insuff.entry_type === 'education' ? 'Education' : 'Employment'} section`);
                      }}
                    >
                      Update in My Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Insufficiencies from QC - Only show open/waiting/responded items */}
      {manualInsufficiencies.filter(i => i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed').length > 0 && (
        <Card className="border-rose-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-rose-800 flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" />
              QC Raised Queries ({manualInsufficiencies.filter(i => i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed').length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {manualInsufficiencies.filter(i => i.status !== 'cleared' && i.status !== 'resolved' && i.status !== 'closed').map((insuff) => {
                const isWaiting = insuff.status === 'waiting_for_clearance';
                const isResponded = insuff.status === 'responded';
                
                return (
                <div 
                  key={insuff.id} 
                  className={`p-4 rounded-lg border ${
                    isWaiting
                      ? 'border-amber-200 bg-amber-50'
                      : isResponded
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-rose-200 bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Badge className="mb-2" variant="outline">
                        {getDetailedCheckType(insuff)}
                      </Badge>
                      <h4 className="font-semibold text-slate-800">
                        {insuff.reason?.replace(/_/g, ' ').toUpperCase()}
                      </h4>
                      <p className="text-slate-600 mt-1">{insuff.description}</p>
                      {/* Show entry details after description */}
                      {insuff.entry_name && (
                        <p className="text-sm text-slate-500 mt-1 font-medium">
                          {insuff.entry_type === 'employment' && `Company: ${insuff.entry_name}`}
                          {insuff.entry_type === 'education' && `Qualification: ${insuff.entry_name}`}
                          {insuff.entry_type === 'address' && `Address Type: ${insuff.entry_name}`}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Raised on: {insuff.created_at ? new Date(insuff.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <Badge className={
                      isWaiting
                        ? 'bg-amber-100 text-amber-700'
                        : isResponded
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-rose-100 text-rose-700'
                    }>
                      {isWaiting ? (
                        <><Clock className="w-3 h-3 mr-1" />Awaiting QC Review</>
                      ) : isResponded ? (
                        <><Clock className="w-3 h-3 mr-1" />Awaiting Review</>
                      ) : (
                        <><AlertTriangle className="w-3 h-3 mr-1" />Action Required</>
                      )}
                    </Badge>
                  </div>

                  {/* Upload section - only show if not waiting */}
                  {!isWaiting && (
                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <div>
                        <Label className="text-sm font-medium">Upload Missing Document(s)</Label>
                        <p className="text-xs text-slate-500 mb-1">You can select multiple files at once</p>
                        <div className="flex gap-2 mt-1">
                          <input
                            type="file"
                            multiple
                            onChange={(e) => handleFileUpload(insuff.id, e.target.files)}
                            className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                            disabled={uploading[insuff.id]}
                          />
                          {uploading[insuff.id] && <span className="text-sm text-slate-500">Uploading...</span>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show uploaded documents */}
                  {insuff.uploaded_documents?.length > 0 && (
                    <div className="mt-3 p-3 bg-emerald-50 rounded border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800 mb-2">📎 Uploaded Documents:</p>
                      <div className="space-y-1">
                        {insuff.uploaded_documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-emerald-700">
                            <FileCheck className="w-4 h-4" />
                            <a 
                              href={doc.file_url?.startsWith('http') ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {doc.filename}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Response section - Always visible to allow updating response */}
                  <div className="mt-3">
                    <Label className="text-sm font-medium">
                      {(insuff.employee_response || insuff.candidate_response) ? 'Update Response' : 'Add Response / Explanation'}
                    </Label>
                    <Textarea
                      value={responses[insuff.id] || insuff.employee_response || insuff.candidate_response || ''}
                      onChange={(e) => handleResponseChange(insuff.id, e.target.value)}
                      placeholder="Explain why this document is not available or provide clarification..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  {/* Waiting for Clearance status */}
                  {isWaiting && (
                    <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" />
                        <p className="text-sm font-medium">Documents submitted - Waiting for QC review</p>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">You can still update your response above if needed</p>
                    </div>
                  )}
                </div>
              )})}
            </div>
            
            {/* Submit All Responses Button */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <Button 
                onClick={handleSubmitAllResponses}
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={submittingAll}
              >
                {submittingAll ? (
                  <>Submitting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Submit All Responses</>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-2 text-center">
                This will submit responses for all queries above
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cleared Insufficiencies History */}
      {clearedInsufficiencies.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-emerald-800 flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5" />
              Cleared Queries ({clearedInsufficiencies.length})
            </CardTitle>
            <p className="text-sm text-emerald-600">Previously raised queries that have been resolved</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {clearedInsufficiencies.map((insuff) => (
                <div 
                  key={insuff.id} 
                  className="p-3 rounded-lg border border-emerald-200 bg-white"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge className="mb-1" variant="outline">
                        {getDetailedCheckType(insuff)}
                      </Badge>
                      <h4 className="font-medium text-slate-700 text-sm">
                        {insuff.reason?.replace(/_/g, ' ')}
                      </h4>
                      {/* Show entry details for cleared items */}
                      {insuff.entry_name && (
                        <p className="text-xs text-slate-600 mt-0.5">
                          {insuff.entry_type === 'employment' && `Company: ${insuff.entry_name}`}
                          {insuff.entry_type === 'education' && `Qualification: ${insuff.entry_name}`}
                          {insuff.entry_type === 'address' && `Address Type: ${insuff.entry_name}`}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        Raised: {insuff.created_at ? new Date(insuff.created_at).toLocaleDateString() : 'N/A'}
                        {insuff.resolved_at && ` • Cleared: ${new Date(insuff.resolved_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />Cleared
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No insufficiencies */}
      {!hasInsufficiencies && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
            <h3 className="text-xl font-semibold text-emerald-700">No Pending Actions</h3>
            <p className="text-emerald-600 mt-2">All your information is complete. No action required at this time.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
